import { useContext, useMemo } from "react";
import currency from "currency.js";
import { BigNumber, ethers } from "ethers";
import useSubgraphData from "../subgraph/useSubgraphData";
import { useEmissions } from "../emissions/useEmissions";
import {
  calculateStakingApr,
  calculateStakingRewardsAndHistory,
  getCurrentEpoch,
  getUnlockDateOfEpoch,
} from "../../../utils/math";
import { IStat } from "../../../interfaces/Toast";
import { AuthContext } from "../../../contexts/AuthContext";
import useAevoStaking from "../../contracts/useAevoStaking";
import useWallet from "../../wallet/useWallet";

export type BadgeTier = "Bronze" | "Silver" | "Gold" | "Platinum" | "Diamond";

const useRewards = () => {
  const { account } = useContext(AuthContext);
  const { data: emissions } = useEmissions();
  const decimals = 18;
  const { accountStakingData, vaultStakingData, fetchSubgraphData } =
    useSubgraphData();

  const wallet = useWallet();
  const { isAccountWhitelisted } = useAevoStaking(wallet, true);

  const [confirmedStakingRewards, stakingEpochRewardsHistory] = useMemo(() => {
    if (!emissions || !account) {
      return [undefined, undefined];
    }

    const stakingRewardsAndHistory = calculateStakingRewardsAndHistory(
      accountStakingData?.epochStakeAmounts || [],
      vaultStakingData?.epochStakeAmounts || [],
      emissions,
      account
    );
    return [
      stakingRewardsAndHistory.confirmedRewards,
      stakingRewardsAndHistory.rewardsHistoryArray,
    ];
  }, [
    account,
    accountStakingData?.epochStakeAmounts,
    emissions,
    vaultStakingData?.epochStakeAmounts,
  ]);

  const unstakeTimingsV1: IStat[] = useMemo(() => {
    const currentEpoch = getCurrentEpoch();
    const accountStakeAmounts = accountStakingData?.epochStakeAmounts || [];
    return (
      accountStakeAmounts
        .filter(
          (stake) =>
            stake.epoch > currentEpoch - 9 && stake.stakedAmountV1 !== "0"
        )
        // only show epochs you cannot unstake yet
        .map((stake) => ({
          label: getUnlockDateOfEpoch(stake.epoch),
          value: currency(
            ethers.utils.formatUnits(stake.stakedAmountV1, decimals),
            {
              symbol: "",
            }
          ).format(),
        }))
    );
  }, [accountStakingData]);

  const unstakeTimingsV2: IStat[] = useMemo(() => {
    const currentEpoch = getCurrentEpoch();
    const V2_START_EPOCH = 83; // V2 staking starts from epoch 83
    const accountStakeAmounts = accountStakingData?.epochStakeAmounts || [];

    // If whitelisted, V2 amounts can be unstaked anytime, so no locked timings
    if (isAccountWhitelisted) {
      return [];
    }

    return (
      accountStakeAmounts
        .filter(
          (stake) =>
            stake.epoch >= V2_START_EPOCH &&
            stake.epoch > currentEpoch - 9 &&
            stake.stakedAmountV2 !== "0"
        )
        // only show epochs you cannot unstake yet
        .map((stake) => ({
          label: getUnlockDateOfEpoch(stake.epoch),
          value: currency(
            ethers.utils.formatUnits(stake.stakedAmountV2, decimals),
            {
              symbol: "",
            }
          ).format(),
        }))
    );
  }, [accountStakingData, isAccountWhitelisted]);

  // total staked amount for an account (V1 + V2)
  const stakedAmount = useMemo(() => {
    if (!accountStakingData) {
      return undefined;
    }
    return ethers.utils.parseUnits(accountStakingData.totalStakedAmount, 0);
  }, [accountStakingData]);

  const intendedStakedAmount = useMemo(() => {
    if (!accountStakingData) {
      return undefined;
    }
    return ethers.utils.parseUnits(accountStakingData.intendedStakedAmount, 0);
  }, [accountStakingData]);
  // total staked amount for in the vault (V1 + V2)
  const totalStakedAmount = useMemo(() => {
    if (!vaultStakingData) {
      return undefined;
    }
    return ethers.utils.parseUnits(vaultStakingData.intendedStakedAmount, 0);
  }, [vaultStakingData]);

  const stakingApr = useMemo(() => {
    if (!emissions || !totalStakedAmount) {
      return undefined;
    }

    return calculateStakingApr(emissions, totalStakedAmount);
  }, [emissions, totalStakedAmount]);

  const unstakableAmountV1 = useMemo(() => {
    const currentEpoch = getCurrentEpoch();
    const accountStakeAmounts = accountStakingData?.epochStakeAmounts || [];

    return accountStakeAmounts
      .filter((stake) => stake.epoch <= currentEpoch - 9)
      .reduce((total, stake) => {
        if (stake.unstakeEpochV1 === 9999) {
          return (
            total +
            parseFloat(ethers.utils.formatUnits(stake.stakedAmountV1, decimals))
          );
        }
        return total;
      }, 0);
  }, [accountStakingData]);

  const unstakableAmountV2 = useMemo(() => {
    const currentEpoch = getCurrentEpoch();
    const V2_START_EPOCH = 83; // V2 staking starts from epoch 83
    const accountStakeAmounts = accountStakingData?.epochStakeAmounts || [];

    return accountStakeAmounts
      .filter(
        (stake) =>
          stake.epoch >= V2_START_EPOCH &&
          // If whitelisted, all V2 amounts are unstakeable, otherwise only after 9 epochs
          (isAccountWhitelisted || stake.epoch <= currentEpoch - 9)
      )
      .reduce((total, stake) => {
        // For whitelisted accounts, calculate remaining balance (staked - unstaked)
        if (isAccountWhitelisted) {
          const remaining =
            parseFloat(
              ethers.utils.formatUnits(stake.stakedAmountV2, decimals)
            ) -
            parseFloat(
              ethers.utils.formatUnits(stake.unstakedAmountV2, decimals)
            );
          return total + Math.max(0, remaining);
        }

        // For non-whitelisted accounts, only include if not yet unstaked
        if (stake.unstakeEpochV2 === 9999) {
          return (
            total +
            parseFloat(ethers.utils.formatUnits(stake.stakedAmountV2, decimals))
          );
        }
        return total;
      }, 0);
  }, [accountStakingData, isAccountWhitelisted]);

  // Combined unstakable amount for backward compatibility
  const unstakableAmount = useMemo(
    () => unstakableAmountV1 + unstakableAmountV2,
    [unstakableAmountV1, unstakableAmountV2]
  );

  const stakingBadge: BadgeTier | undefined = useMemo(() => {
    const stakedAmountNumber = Number(
      ethers.utils.formatUnits(stakedAmount || 0)
    );
    if (stakedAmountNumber < 1) {
      return undefined;
    }
    if (stakedAmountNumber >= 1 && stakedAmountNumber < 101) {
      return "Bronze";
    }
    if (stakedAmountNumber < 1001) {
      return "Silver";
    }
    if (stakedAmountNumber < 10001) {
      return "Gold";
    }
    if (stakedAmountNumber < 101000) {
      return "Platinum";
    }
    return "Diamond";
  }, [stakedAmount]);

  return {
    confirmedStakingRewards,
    fetchStakingRewards: fetchSubgraphData,
    stakingEpochRewardsHistory,
    unstakeTimingsV1,
    unstakeTimingsV2,
    stakedAmount,
    intendedStakedAmount,
    totalStakedAmount,
    stakingApr,
    unstakableAmount,
    unstakableAmountV1,
    unstakableAmountV2,
    isAccountStaked: stakedAmount && stakedAmount.gt(BigNumber.from(0)),
    stakingBadge,
  };
};

export default useRewards;
